import * as fns from 'date-fns';
import UserAdminType from '../enum/UserAdminType';
import User from '../model/User';
import { PermissionError, ValidationError } from '../service/Errors';

export { TestCNPJ, blobFileToBase64, utf8Decode, utf8Encode } from './oldUtils';

type SortFunction<T> = (a: T[keyof T], b: T[keyof T]) => number;

export function sortObjectArrayByProp<T>(array: Array<T>, propName: keyof T | (keyof T)[], compareFn?: SortFunction<T>) {
    return array.sort((a, b) => {
        if (Array.isArray(propName)) {
            for (var i = 0; i < propName.length; i++) {
                var p = propName[i];
                if (typeof compareFn === 'function') {
                    const result = compareFn(a[p], b[p]);
                    if (result !== 0) return result;
                }
                if (a[p] > b[p]) {
                    return 1;
                }
                if (a[p] < b[p]) {
                    return -1;
                }
            }
        } else {
            if (typeof compareFn === 'function') {
                return compareFn(a[propName], b[propName]);
            }
            if (a[propName] > b[propName]) {
                return 1;
            }
            if (a[propName] < b[propName]) {
                return -1;
            }
        }
        return 0;
    });
}

function getNestedPropValue(obj: any, propPath: string): any {
    return propPath.split('.').reduce((acc, part) => acc && acc[part], obj);
}

export function sortObjectArrayByPropNested<T>(array: Array<T>, propName: string | string[], compareFn?: SortFunction<T>) {
    return array.sort((a, b) => {
        if (Array.isArray(propName)) {
            for (var i = 0; i < propName.length; i++) {
                var p = propName[i];
                const aValue = getNestedPropValue(a, p);
                const bValue = getNestedPropValue(b, p);
                if (typeof compareFn === 'function') {
                    const result = compareFn(aValue, bValue);
                    if (result !== 0) return result;
                } else {
                    if (aValue > bValue) {
                        return 1;
                    }
                    if (aValue < bValue) {
                        return -1;
                    }
                }
            }
        } else {
            const aValue = getNestedPropValue(a, propName);
            const bValue = getNestedPropValue(b, propName);
            if (typeof compareFn === 'function') {
                return compareFn(aValue, bValue);
            } else {
                if (aValue > bValue) {
                    return 1;
                }
                if (aValue < bValue) {
                    return -1;
                }
            }
        }
        return 0;
    });
}

export function sortObjectArrayByPropDate<T>(array: Array<T>, propname: keyof T, desc: boolean = false) {
    return array.sort((a, b) => {
        var propA = a[propname];
        var propB = b[propname];
        if ((typeof propA === 'string' || propA instanceof Date) && (typeof propB === 'string' || propB instanceof Date)) {
            var dateA = new Date(propA);
            var dateB = new Date(propB);
            if (dateA > dateB) {
                return desc ? -1 : 1;
            }
            if (dateA < dateB) {
                return desc ? 1 : -1;
            }
        }
        return 0;
    });
}

export function MapToArray<T>(map: Map<any, T>): T[] {
    return Array.from(map).map(([_, value]) => value);
}

export function groupByProp<T>(array: Array<T>, prop: keyof T) {
    var map = new Map<string, T[]>();
    array.forEach(item => {
        var value = item[prop];
        var val = typeof value === 'object' ? JSON.stringify(value) : String(value);
        if (map.has(val)) {
            map.get(val)?.push(item);
        } else {
            map.set(val, [item]);
        }
    });
    return map;
}


export function removeDuplicated<T>(array: Array<T>) {
    return array.filter((v, i, a) => a.lastIndexOf(v) === i);
}

export function accentedPhrase(phrase: string) {
    const phraseArray = phrase.toLowerCase().split(' ');
    const phraseSanitized = phraseArray.map(sanitizePhrase);
    console.log(phraseSanitized);
}

function sanitizePhrase(phrase: string) {
    const charArray = phrase.toLowerCase().split('');
    const charsAccented = charArray.map(char => chars(char));
    const phraseCount = charArray.length;
    const phrases = [phrase];

    for (var phraseIndex = 0; phraseIndex < phraseCount; phraseIndex++) {
        var char = charsAccented[phraseIndex];
        if (char) {
            var charCount = char.length;
            for (var charIndex = 0; charIndex < charCount; charIndex++) {
                if (phraseIndex === 0) {
                    phrases.push(char[charIndex] + String(phrase).substr(1, phraseCount));
                } else {
                    phrases.push(String(phrase).substr(0, phraseIndex) + char[charIndex] + String(phrase).substr(phraseIndex + 1, phraseCount));
                }
            }
        }
    }
    return phrases;
}

function chars(char: string) {
    const charsAccented: Record<string, string[]> = {
        'a': ['á', 'â', 'ã'],
        'á': ['a', 'â', 'ã'],
        'â': ['á', 'a', 'ã'],
        'ã': ['á', 'â', 'a'],

        'e': ['é', 'ê'],
        'é': ['e', 'ê'],
        'ê': ['é', 'e'],

        'i': ['í', 'î'],
        'í': ['i', 'î'],
        'î': ['í', 'i'],

        'o': ['ó', 'ô', 'õ'],
        'ó': ['o', 'ô', 'õ'],
        'ô': ['ó', 'o', 'õ'],
        'õ': ['ó', 'ô', 'o'],

        'u': ['ú', 'û'],
        'ú': ['u', 'û'],
        'û': ['ú', 'u'],

        'c': ['ç'],
        'ç': ['c'],

        'n': ['ñ'],
        'ñ': ['n'],
    }
    return charsAccented[char];
}

export async function delay(milliseconds: number) {
    return new Promise((resolve, reject) => {
        window.setTimeout(resolve, milliseconds);
    });
}

export function objectEquals(a: any, b: any) {
    const _objectEquals = (a: any, b: any, deepCount: number) => {
        if (!a && !b) return true;
        if (typeof a !== 'object' || typeof b !== 'object') {
            return a === b;
        }
        if (deepCount > 50) return false;
        const props = Object.getOwnPropertyNames(a);
        for (var i = 0; i < props.length; i++) {
            var propName = props[i];
            var propA = a[propName], propB = b[propName];
            if (propA === propB) {
                continue;
            }
            if (typeof propA === 'object' && typeof propB === 'object') {
                if (_objectEquals(propA, propB, deepCount + 1)) {
                    continue;
                }
            }
            return false;
        }
        return true;
    }
    try {
        return _objectEquals(a, b, 1) && _objectEquals(b, a, 1);
    } catch (e) {
        return false;
    }
}

export function objectClone<T extends object | T[]>(source: T): T {
    if (Array.isArray(source)) {
        return source.map((v) => objectClone(v)) as T;
    } else if (source !== null && typeof source === 'object') {
        const clone = {} as T;
        for (const prop in source) {
            let sourceTemp = source[prop] as T;
            Object.assign(clone, { [prop]: objectClone(sourceTemp) });
        }
        return clone;
    } else {
        return source;
    }
}

export function objectFilter<T extends object>(raw: T, allowed: (keyof T)[]): Partial<T> {
    const result: Partial<T> = {};
    return Object.keys(raw)
        .filter(key => allowed.includes(key as keyof T))
        .map(key => key as keyof T)
        .reduce((obj, key) => {
            obj[key] = raw[key];
            return obj;
        }, result);
}

export const ipRegExp = /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;


export async function asyncPredicate(predicate: () => boolean, timeout = 1000, step = 10) {
    if (typeof predicate !== 'function') return false;
    for (var i = 0; i < (timeout / step); i++) {
        if (predicate()) return true;
        await delay(step);
    }
    return false;
}

export async function windowNotity(message: string, onclick: () => any) {
    var permission = window.Notification.permission;
    if (permission !== 'granted') {
        permission = await window.Notification.requestPermission();
    }
    if (permission === 'granted') {
        const notification = new window.Notification('Commbox SafeAccess Lite', { body: message });
        notification.onclick = onclick;
        return true;
    }
    return false;
}

export function getReactElementText(element: React.ReactElement): string {
    const pieces = [];
    if (typeof element === 'string' || typeof element === 'number') {
        pieces.push(element);
    } else if (typeof element === 'object') {
        if (!element) return '';
        const { props } = element;
        if (typeof props === 'object') {
            const { children } = props;
            if (Array.isArray(children)) {
                pieces.push(...children.map(c => getReactElementText(c)));
            } else if (typeof children === 'string') {
                pieces.push(children);
            }
        }
    }
    return pieces.join('');
}

export function popupwindow(url: string | URL, title: string, w: number, h: number) {
    var left = (window.screen.width / 2) - (w / 2);
    var top = (window.screen.height / 2) - (h / 2);
    return window.open(url, title, 'toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=' + w + ', height=' + h + ', top=' + top + ', left=' + left);
}

export function downloadBlob(blob: Blob, filename: string) {
    var a = window.document.createElement("a");
    var url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();  // IE: "Access is denied"; see: https://connect.microsoft.com/IE/feedback/details/797361/ie-10-treats-blob-url-as-cross-origin-and-denies-access
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

export function getDateDays(dtIni: Date, dtEnd: Date) {
    var diferencaDias = fns.differenceInDays(dtEnd, dtIni);
    var dates = [];
    var currentDate = dtIni;
    for (var d = 0; d <= diferencaDias; d++) {
        dates.push(currentDate);
        currentDate = fns.addDays(currentDate, 1);
    }
    return dates;
}

export function TestCPF(strCPF: string) {
    var Soma = 0;
    var Resto = 0;
    strCPF = strCPF.replace(/(\.|-)/g, '');
    if (Number(strCPF) == 0 || Number(strCPF) == 99999999999) return false;
    for (let i = 1; i <= 9; i++) Soma = Soma + parseInt(strCPF.substring(i - 1, i)) * (11 - i);
    Resto = (Soma * 10) % 11;

    if ((Resto == 10) || (Resto == 11)) Resto = 0;
    if (Resto != parseInt(strCPF.substring(9, 10))) return false;

    Soma = 0;
    for (let i = 1; i <= 10; i++) Soma = Soma + parseInt(strCPF.substring(i - 1, i)) * (12 - i);
    Resto = (Soma * 10) % 11;

    if ((Resto == 10) || (Resto == 11)) Resto = 0;
    if (Resto != parseInt(strCPF.substring(10, 11))) return false;
    return true;
}


export const fnsFormatFullDate = 'dd/MM/yyyy HH:mm:ss';

export const safePasswordRegex = /^(((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{8,})/; //Letras e numeros, minimo oito caracteres

/**
 * 
 * @param {string} strMacAddress 
 */
export function TestMacAddress(strMacAddress: string) {
    if (/^([0-9a-fA-F]{2}-){5}[0-9a-fA-F]{2}$/.exec(strMacAddress)) {
        return true;
    }
    return false;
}

export function insertBetween<T>(array: T[], item: T) {
    var result: T[] = [];
    array.forEach((v, i) => {
        if (i > 0) {
            result.push(item);
        }
        result.push(v);
    });
    return result;
};

export function firstUppercase(value: string) {
    if (value === '') return '';
    var f = value[0];
    return f.toUpperCase() + value.substring(1);
}

export function firstLowercase(value: string) {
    if (value === '') return '';
    var f = value[0];
    return f.toLowerCase() + value.substring(1);
}


export function getFirstLettersToAvatar(name: string) {
    var matches = name.toUpperCase().match(/\b(\w)/g)?.slice(0, 2) || [];
    return matches.join('');
}

export function removerAcentos(s: string) {
    return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export function validateDate(dateString: string) {
    // First check for the pattern -- mm/dd/yyyy
    if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString))
        return false;

    // Parse the date parts to integers
    var parts = dateString.split("/");
    var day = parseInt(parts[1], 10);
    var month = parseInt(parts[0], 10);
    var year = parseInt(parts[2], 10);

    // Check the ranges of month and year
    if (year < 1000 || year > 3000 || month == 0 || month > 12)
        return false;

    var monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    // Adjust for leap years
    if (year % 400 == 0 || (year % 100 != 0 && year % 4 == 0))
        monthLength[1] = 29;

    // Check the range of the day
    return day > 0 && day <= monthLength[month - 1];
};

export function parseQueryString(url: string) {
    const queryString = url.slice(url.indexOf('?') + 1);
    const paramArr = queryString.split('&');
    const result: Record<string, string> = {};
    paramArr.map(param => {
        const [key, val] = param.split('=');
        result[key] = decodeURIComponent(val);
    })
    return result;
}

export function convertBase64ToBlob(base64Image: string) {
    // Split into two parts
    base64Image = "data:image/png;base64," + base64Image;
    const parts = base64Image.split(';base64,');

    // Hold the content type
    const imageType = parts[0].split(':')[1];

    // Decode Base64 string
    const decodedData = window.atob(parts[1]);

    // Create UNIT8ARRAY of size same as row data length
    const uInt8Array = new Uint8Array(decodedData.length);

    // Insert all character code into uInt8Array
    for (let i = 0; i < decodedData.length; ++i) {
        uInt8Array[i] = decodedData.charCodeAt(i);
    }

    // Return BLOB image after conversion
    return new Blob([uInt8Array], { type: imageType });
}


export function nl2br(str: string) {
    return str.replace(/(?:\r\n|\r|\n)/g, '<br>');
}

export function snakeToCamel(str: string) {
    return str.toLowerCase().replace(/([-_][a-z])/g, group =>
        group
            .toUpperCase()
            .replace('-', '')
            .replace('_', '')
    );
}

export function fnsSetTime(date: Date, hours: number, minutes: number, seconds: number) {
    return fns.set(date, { hours, minutes, seconds, milliseconds: 0 });
}

export function formatCurrency(value: number): string {
    return value.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

export function convertFileByteToSizeString(tamanho: number): string {
    if (tamanho < 1024) {
        return tamanho + " bytes";
    } else if (tamanho < 1048576) {
        const tamanhoKB = (tamanho / 1024).toFixed(2);
        return tamanhoKB + " KB";
    } else {
        const tamanhoMB = (tamanho / 1048576).toFixed(2);
        return tamanhoMB + " MB";
    }
}

export async function retry<T>(fn: () => Promise<T>, retriesLeft = 5, interval = 1000): Promise<T> {
    try {
        const val = await fn();
        return val;
    } catch (error) {
        if (error instanceof PermissionError) {
            throw error;
        }
        if (error instanceof ValidationError) {
            throw error;
        }
        if (retriesLeft) {
            await new Promise(r => setTimeout(r, interval));
            return await retry(fn, retriesLeft - 1, interval);
        } else {
            throw error;
        }
    }
}

export function multiSearchFilter(searchSplit: string[]): string[][] {
    if (searchSplit.length === 1) {
        return [searchSplit];
    }

    const arrSearch: string[][] = [];

    for (let i = 0; i < searchSplit.length; i++) {
        const elementoAtual = searchSplit[i];
        const elementosRestantes = searchSplit.filter((_, index) => index !== i);
        const combinacoesRestantes = multiSearchFilter(elementosRestantes);

        for (let j = 0; j < combinacoesRestantes.length; j++) {
            arrSearch.push([elementoAtual, ...combinacoesRestantes[j]]);
        }
    }

    return arrSearch;
}

export function isAdminOrSuperAdmin(user?: User): boolean {
    return [UserAdminType.SuperAdmin, UserAdminType.Admin].includes(user?.admin_type || 0);
}

export function timeStringToMinutes(timeString: string): number {
    const timeRegex = /^(\d{1,2}):(\d{2})$/;

    const match = timeString.match(timeRegex);
    if (match === null) {
        return 0; // Retorna null se a string não estiver no formato esperado
    }

    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);

    if (isNaN(hours) || isNaN(minutes)) {
        return 0; // Retorna null se não for possível converter as horas ou minutos em números
    }

    const totalMinutes = hours * 60 + minutes;
    return totalMinutes;
}

export function minutesToTimeString(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    const hoursStr = hours.toString().padStart(2, '0');
    const minsStr = mins.toString().padStart(2, '0');

    return `${hoursStr}:${minsStr}`;
}

export function getUserDepartmentPermissions(user: User | undefined) {
    const permissions = {
        perm_menu_projects: false,
        perm_menu_tasks: false,
        perm_menu_all_open: false,
        perm_menu_post_service: false
    };
    if (user) {
        if (user.department) {
            if (user.department.perm_menu_projects) {
                permissions.perm_menu_projects = true;
            }
            if (user.department.perm_menu_tasks) {
                permissions.perm_menu_tasks = true;
            }
            if (user.department.perm_menu_all_open) {
                permissions.perm_menu_all_open = true;
            }
            if (user.department.perm_menu_post_service) {
                permissions.perm_menu_post_service = true;
            }
        }
        if (user.departments) {
            user.departments.forEach(department => {
                if (department.perm_menu_projects) {
                    permissions.perm_menu_projects = true;
                }
                if (department.perm_menu_tasks) {
                    permissions.perm_menu_tasks = true;
                }
                if (department.perm_menu_all_open) {
                    permissions.perm_menu_all_open = true;
                }
                if (department.perm_menu_post_service) {
                    permissions.perm_menu_post_service = true;
                }
            });
        }
    }
    return permissions;
}

export function getUserDepartmentsIds(user?: User): number[] {
    const departments = [...(user?.departments || [])];
    if (user?.department) departments.push(user.department);
    return departments.map(d => d.id);
}

export function normalizeString(str: string): string {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

// Retorna um mapa onde a chave é o nível atual e o valor é a experiência necessária para alcançar o próximo nível.
// Para o nível 50 retorna null indicando que é o nível máximo.
export function getExperienceNeedleByLevel(): Record<number, number | null> {
    return {
        1: 50,    // antes: 50   | Δ 0.00%
        2: 100,   // antes: 50   | Δ +100.00% (exceção solicitada)
        3: 115,   // antes: 115  | Δ 0.00%
        4: 190,   // antes: 190  | Δ 0.00%
        5: 275,   // antes: 275  | Δ 0.00%
        6: 370,   // antes: 370  | Δ 0.00%
        7: 475,   // antes: 475  | Δ 0.00%
        8: 600,   // antes: 590  | Δ +1.69%
        9: 700,   // antes: 715  | Δ -2.10%
        10: 850,   // antes: 850  | Δ 0.00%
        11: 1000,  // antes: 995  | Δ +0.50%
        12: 1200,  // antes: 1150 | Δ +4.35%
        13: 1300,  // antes: 1315 | Δ -1.14%
        14: 1500,  // antes: 1490 | Δ +0.67%
        15: 1700,  // antes: 1675 | Δ +1.49%
        16: 1900,  // antes: 1870 | Δ +1.60%
        17: 2100,  // antes: 2075 | Δ +1.20%
        18: 2300,  // antes: 2290 | Δ +0.44%
        19: 2500,  // antes: 2515 | Δ -0.60%
        20: 2800,  // antes: 2750 | Δ +1.82%
        21: 3000,  // antes: 2995 | Δ +0.17%
        22: 3300,  // antes: 3250 | Δ +1.54%
        23: 3500,  // antes: 3515 | Δ -0.43%
        24: 3800,  // antes: 3790 | Δ +0.26%
        25: 4100,  // antes: 4075 | Δ +0.61%
        26: 4400,  // antes: 4370 | Δ +0.69%
        27: 4700,  // antes: 4675 | Δ +0.53%
        28: 5000,  // antes: 4990 | Δ +0.20%
        29: 5300,  // antes: 5315 | Δ -0.28%
        30: 5700,  // antes: 5650 | Δ +0.88%
        31: 6000,  // antes: 5995 | Δ +0.08%
        32: 6400,  // antes: 6350 | Δ +0.79%
        33: 6700,  // antes: 6715 | Δ -0.22%
        34: 7100,  // antes: 7090 | Δ +0.14%
        35: 7500,  // antes: 7475 | Δ +0.33%
        36: 7900,  // antes: 7870 | Δ +0.38%
        37: 8300,  // antes: 8275 | Δ +0.30%
        38: 8700,  // antes: 8690 | Δ +0.12%
        39: 9100,  // antes: 9115 | Δ -0.16%
        40: 9600,  // antes: 9550 | Δ +0.52%
        41: 10000, // antes: 9995 | Δ +0.05%   (múltiplo de 1000 :dart:)
        42: 10500, // antes: 10450| Δ +0.48%
        43: 10900, // antes: 10915| Δ -0.14%
        44: 11400, // antes: 11390| Δ +0.09%
        45: 11900, // antes: 11875| Δ +0.21%
        46: 12400, // antes: 12370| Δ +0.24%
        47: 12900, // antes: 12875| Δ +0.19%
        48: 13400, // antes: 13390| Δ +0.07%
        49: 13900, // antes: 13915| Δ -0.11%
        50: null,  // nível máximo (não há próximo)
    };
}